/**
 * Agent Usage Chart component for dashboard
 * 
 * Displays a bar chart showing usage metrics by agent (conversations, messages, tool calls).
 */

import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

export interface AgentUsage {
  agentName: string;
  conversations: number;
  messages: number;
  toolCalls: number;
}

interface AgentUsageChartProps {
  data: AgentUsage[];
  title?: string;
}

export function AgentUsageChart({
  data,
  title = 'Usage by Agent',
}: AgentUsageChartProps) {
  const chartData = useMemo(() => {
    return data.slice(0, 10).map((item) => ({
      name: item.agentName.length > 15 
        ? item.agentName.slice(0, 15) + '...' 
        : item.agentName,
      conversations: item.conversations,
      messages: item.messages,
      toolCalls: item.toolCalls,
    }));
  }, [data]);

  if (data.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] flex items-center justify-center text-muted-foreground">
            No agent usage data available
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="name" 
                tick={{ fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis />
              <Tooltip />
              <Legend />
              <Bar dataKey="conversations" fill="#8884d8" name="Conversations" />
              <Bar dataKey="messages" fill="#82ca9d" name="Messages" />
              <Bar dataKey="toolCalls" fill="#ffc658" name="Tool Calls" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
